// Laying out the arc. Angles are not hand-placed: the windows are the sizes the app makes them, so their places
// follow from their widths. The centre window sits at 0°; each next one is placed just past the previous one's
// edge, alternating right and left, with a gap. This is the arrangement maths the WebXR client needs for real, so
// it lives here rather than in a list of numbers that go stale the moment a size changes.
//
// `arrange(specs, { RADIUS, isLive }, gap)`: every spec's `angle` and `lift` set (a spec that was dragged by hand,
// `placed`, stays where it was put); `isLive(id)` says whether a window is in the arc, for the rule-placed windows
// to be laid past each other.
export function arrange(specs, { RADIUS, isLive }, gap = 0.18) {
  // the fixed windows in their order, then whatever was added later (streams), in the order they came
  const sequence = [...specs.filter((w) => w.kind === 'session' || w.winKind === 'session'), ...['home', 'macapps'].map((id) => specs.find((w) => w.id === id)).filter(Boolean), ...specs.filter((w) => w.kind === 'html' && w.winKind !== 'session' && !w.rule), ...specs.filter((w) => w.stream)]
  const edges = { right: 0, left: 0 }   // arc length already used on each side, from the centre
  sequence.forEach((spec, i) => {
    if (spec.placed) return   // dragged by hand: it stays where it was put
    const r = spec.radius ?? RADIUS
    if (i === 0) { spec.angle = 0; edges.right = edges.left = spec.w / 2 + gap; return }
    const side = i % 2 === 1 ? 'right' : 'left'
    const along = edges[side] + spec.w / 2          // arc length from the centre to this window's middle
    spec.angle = (along / r) * (180 / Math.PI) * (side === 'right' ? -1 : 1)
    edges[side] = along + spec.w / 2 + gap
  })
  // the rest open where the headset opens them: beside, above or below their window, or in front of it (a sheet)
  const used = new Map()   // "anchor side" → metres already taken on that side
  const done = new Set()
  for (const spec of specs.filter((w) => w.rule && !w.placed)) {
    done.add(spec)
    const [of, side] = spec.rule
    const anchor = of === 'session' ? [...specs].reverse().find((w) => w.winKind === 'session' || w.kind === 'session') || specs.find((w) => w.id === 'home') : specs.find((w) => w.id === of)
    if (!anchor) continue
    const r = spec.radius ?? RADIUS, key = anchor.id + ' ' + side, taken = used.get(key) || 0
    if (side === 'front') { spec.angle = anchor.angle; spec.lift = anchor.lift + taken; used.set(key, taken + 0.04); continue }
    if (side === 'trailing' || side === 'leading') {
      const along = anchor.w / 2 + gap + taken + spec.w / 2
      spec.angle = anchor.angle + (along / r) * (180 / Math.PI) * (side === 'trailing' ? -1 : 1)
      spec.lift = anchor.lift
      used.set(key, taken + spec.w + gap)
    } else {
      spec.angle = anchor.angle
      const off = anchor.h / 2 + gap + taken + spec.h / 2
      spec.lift = anchor.lift + (side === 'above' ? off : -off)
      used.set(key, taken + spec.h + gap)
    }
    // past any window already there, on the same side
    const rect = (w) => { const a = ((w.angle || 0) * Math.PI / 180) * (w.radius ?? RADIUS); return { l: a - w.w / 2, r: a + w.w / 2, b: (w.lift || 0) - w.h / 2, t: (w.lift || 0) + w.h / 2 } }
    const others = specs.filter((w) => w !== spec && (!w.rule || done.has(w) || w.placed) && (isLive(w.id) || w.stream) && w.rule?.[1] !== 'front')
    for (let tries = 0; tries < 12; tries++) {
      const me = rect(spec)
      const hit = others.find((o) => { const q = rect(o); return me.l < q.r - 0.01 && me.r > q.l + 0.01 && me.b < q.t - 0.01 && me.t > q.b + 0.01 })
      if (!hit) break
      const q = rect(hit)
      if (side === 'trailing') spec.angle -= ((me.r - q.l + gap) / r) * (180 / Math.PI)   // on to the right, past it
      else if (side === 'leading') spec.angle += ((q.r - me.l + gap) / r) * (180 / Math.PI)
      else if (side === 'above') spec.lift += q.t - me.b + gap
      else spec.lift -= me.t - q.b + gap
    }
  }
}
